/**
 * filterQuery — helpers puros compartilhados pelos filtros de Tarefas
 * (Board + Overview) e suas views. Centraliza parsing de query string,
 * comparação de filtros e serialização de URL, evitando duplicação entre
 * `view-task-board.vue` e `view-task-overview.vue` (ambos consomem os filtros
 * via `BasePageHeader` teleportado).
 *
 * Contrato dos `apply*ToQuery`: "se vazio, deleta a chave; se preenchido,
 * escreve" — usado para montar o dict de query antes de serializar a URL.
 */

import {
  visibleListColumns,
  hasBoardFiltersActive,
} from '@modules/tarefas/store/getters'

// Separadores da assinatura de filtros (`taskFilterSignature`). Nomeados para
// não virarem literal solto no meio da serialização.
const SIGNATURE_FIELD_SEPARATOR = '|'
const SIGNATURE_VALUE_SEPARATOR = ','
const SIGNATURE_RANGE_SEPARATOR = '~'

// Parse CSV de int (responsavelId/clientId multi-select). Aceita também
// valor escalar para compat com URLs antigas (`?responsavelId=7`).
export function parseIntCsvQuery(raw) {
  if (raw == null || raw === '' || raw === 'null') return []
  return String(raw)
    .split(',')
    .map(function (v) {
      const t = v.trim()
      if (!t) return null
      const n = Number.parseInt(t, 10)
      return Number.isNaN(n) ? null : n
    })
    .filter(function (n) {
      return n != null
    })
}

// Parse CSV de UUID (tagIds). Mantém ids brutos.
export function parseTagIdsQuery(raw) {
  if (!raw) return []
  return String(raw)
    .split(',')
    .map(function (v) {
      return v.trim()
    })
    .filter(function (v) {
      return !!v
    })
}

export function arraysEqual(a, b) {
  const aa = Array.isArray(a) ? a : []
  const bb = Array.isArray(b) ? b : []
  if (aa.length !== bb.length) return false
  for (let i = 0; i < aa.length; i += 1) {
    if (aa[i] !== bb[i]) return false
  }
  return true
}

// Data completa (DD/MM/YYYY) ou vazia/limpa — usada para liberar o "Filtrar"
// sem disparar com input de data parcial durante a digitação.
export function isCompleteOrCleared(val) {
  if (val == null || val === '') return true
  return /^\d{2}\/\d{2}\/\d{4}$/.test(String(val))
}

export function applyScalarToQuery(next, key, value) {
  if (value) {
    next[key] = value
  } else {
    delete next[key]
  }
}

export function applyArrayToQuery(next, key, value) {
  if (Array.isArray(value) && value.length > 0) {
    next[key] = value.join(',')
  } else {
    delete next[key]
  }
}

// --- Deep-linking helpers (folder/sprint/task em query params) ----------
// Estendem o contrato puro de URL ↔ estado para o Board persistir a lista
// ativa (folder + sprint) e a tarefa aberta (task) na URL — habilitando
// reload-preserva-lista e links compartilháveis. Sem import de Vue/store:
// helpers continuam puros e testáveis isoladamente.

// Parse de id escalar (string trimada) com os mesmos guards de
// `parseIntCsvQuery`: null/''/'null' → null. Usado na hidratação de
// data/busca da URL (startDate/endDate/q continuam em query params).
export function parseScalarIdQuery(raw) {
  if (raw == null || raw === '' || raw === 'null') return null
  const trimmed = String(raw).trim()
  return trimmed || null
}

// Serializa o dict de query params em URL completo (path + ?qs). Pula
// entradas null/empty defensivamente.
export function buildUrlFromQuery(path, queryDict) {
  const params = new URLSearchParams()
  for (const key of Object.keys(queryDict)) {
    const value = queryDict[key]
    if (value != null && value !== '') params.set(key, value)
  }
  const qs = params.toString()
  return qs ? `${path}?${qs}` : path
}

// Shallow-equal das dimensões do boardFilters usadas como chaves de URL —
// evita o loop URL ↔ Vuex (replaceState bump dispararia o hidratador).
export function boardFiltersEqual(a, b) {
  if (!a || !b) return a === b
  if (!arraysEqual(a.responsavelIds, b.responsavelIds)) return false
  if (!arraysEqual(a.clientIds, b.clientIds)) return false
  if (!arraysEqual(a.tagIds, b.tagIds)) return false
  return true
}

export function boardQueryUnchanged(next, currentQuery) {
  return (
    next.responsavelId === currentQuery.responsavelId &&
    next.clientId === currentQuery.clientId &&
    next.tagIds === currentQuery.tagIds &&
    next.startDate === currentQuery.startDate &&
    next.endDate === currentQuery.endDate &&
    next.q === currentQuery.q &&
    // `view` = visão rápida ativa. Sem esta comparação o guard consideraria
    // "inalterado" um write que só troca a visão, e a URL nunca refletiria a
    // troca — o F5 voltaria para a visão anterior.
    next.view === currentQuery.view
  )
}

// --- ClickUp-style short-link PATH helpers ------------------------------
// O Board persiste folder/sprint/task no PATH (estilo ClickUp) em vez de query
// params: `/task/board/list/l/<folderShort>/s/<sprintShort>/t/<taskShort>`.
// `<short>` = primeiros 8 chars do UUID. Os filtros continuam em query params.
// Helpers puros (sem Vue/store) — testáveis isoladamente.

// Comprimento do prefixo curto (truncamento do UUID). Mantido como constante
// para o frontend e o contrato backend (/resolve) ficarem alinhados.
export const SHORTLINK_PREFIX_LENGTH = 8

// Trunca um id (UUID) para os primeiros 8 chars. null/'' → null (a dimensão
// simplesmente não entra no path). NUNCA usa parseInt — preserva a string.
export function truncateShort(id) {
  if (id == null || id === '') return null
  const s = String(id).slice(0, SHORTLINK_PREFIX_LENGTH)
  return s || null
}

// Monta o segmento de short-link a partir dos ids COMPLETOS de folder/sprint/
// task. Segmentos independentes/opcionais da direita: sem folder, nada é
// emitido (sprint/task dependem do folder no contrato hierárquico, mas o
// builder é defensivo e omite a dimensão ausente). Sempre começa com '/'.
//   folder only          → /l/<f>
//   folder + sprint      → /l/<f>/s/<s>
//   folder + sprint+task → /l/<f>/s/<s>/t/<t>
//   folder + task        → /l/<f>/t/<t>
// Retorna '' quando não há nenhuma dimensão (path base puro).
export function buildShortlinkPath({ folder, sprint, task } = {}) {
  const f = truncateShort(folder)
  const s = truncateShort(sprint)
  const t = truncateShort(task)
  const parts = []
  if (f != null) {
    parts.push('l', f)
    if (s != null) parts.push('s', s)
  }
  if (t != null) parts.push('t', t)
  if (parts.length === 0) return ''
  return '/' + parts.join('/')
}

// Faz o parse de uma string de suffix de short-link (ex.: 'l/2eaf1445/s/ba464a1a/t/3f9a0011'
// ou com '/' inicial) em `{ folderShort, sprintShort, taskShort }`. Tokens
// desconhecidos são ignorados; dimensão ausente → null. Tolerante a '/' extra.
export function parseShortlink(shortlink) {
  const out = { folderShort: null, sprintShort: null, taskShort: null }
  if (!shortlink) return out
  const tokens = String(shortlink)
    .split('/')
    .map(function (t) {
      return t.trim()
    })
    .filter(function (t) {
      return !!t
    })
  for (let i = 0; i < tokens.length; i += 1) {
    const key = tokens[i]
    const value = tokens[i + 1]
    if (key === 'l' && value) {
      out.folderShort = value
      i += 1
    } else if (key === 's' && value) {
      out.sprintShort = value
      i += 1
    } else if (key === 't' && value) {
      out.taskShort = value
      i += 1
    }
  }
  return out
}

// Extrai o suffix de short-link de um pathname completo do board, isolando o
// trecho a partir do primeiro token `l/` ou `t/`. Retorna `{ basePath, shortlink }`:
//   /task/board/list/l/2eaf1445/s/ba464a1a
//     → { basePath: '/task/board/list', shortlink: 'l/2eaf1445/s/ba464a1a' }
//   /task/board/list
//     → { basePath: '/task/board/list', shortlink: '' }
// `basePath` é o pathname SEM o suffix (para reescrever a URL preservando a
// view ativa list/kanban/gantt). Idempotente: chamar sobre um basePath puro
// devolve shortlink ''.
export function splitShortlinkPathname(pathname) {
  const path = String(pathname || '')
  const segments = path.split('/')
  let cutIndex = -1
  for (let i = 0; i < segments.length; i += 1) {
    if (segments[i] === 'l' || segments[i] === 't') {
      cutIndex = i
      break
    }
  }
  if (cutIndex === -1) {
    return { basePath: path, shortlink: '' }
  }
  const baseSegments = segments.slice(0, cutIndex)
  const shortSegments = segments.slice(cutIndex).filter(function (s) {
    return !!s
  })
  let basePath = baseSegments.join('/')
  if (!basePath) basePath = '/'
  return { basePath: basePath, shortlink: shortSegments.join('/') }
}

// Parse direto do contexto (shorts) a partir de um pathname completo do board.
// Conveniência que combina `splitShortlinkPathname` + `parseShortlink`.
export function parseShortlinkContextFromPathname(pathname) {
  const split = splitShortlinkPathname(pathname)
  return parseShortlink(split.shortlink)
}

// Guard contra o loop URL ↔ Vuex para o PATH de short-link. Compara o path
// completo (com suffix) que SERIA escrito contra o pathname atual — evita um
// replaceState redundante quando nada mudou no contexto path-based.
export function shortlinkPathUnchanged(nextPath, currentPathname) {
  return String(nextPath || '') === String(currentPathname || '')
}

// Monta a URL completa do board: basePath + suffix de short-link + querystring
// (filtros). Reusa `buildUrlFromQuery` para a metade de query (encoda via
// URLSearchParams e dropa chaves null/empty). `basePath` deve ser o pathname
// SEM suffix (use `splitShortlinkPathname(...).basePath`).
export function buildBoardUrl({ basePath, folder, sprint, task, query } = {}) {
  const suffix = buildShortlinkPath({ folder: folder, sprint: sprint, task: task })
  const fullPath = String(basePath || '') + suffix
  return buildUrlFromQuery(fullPath, query || {})
}

export function overviewFiltersEqual(a, b) {
  if (!a || !b) return a === b
  return (
    a.startDate === b.startDate &&
    a.endDate === b.endDate &&
    arraysEqual(a.responsavelIds, b.responsavelIds) &&
    arraysEqual(a.clientIds, b.clientIds)
  )
}

export function overviewQueryUnchanged(next, currentQuery) {
  return (
    next.startDate === currentQuery.startDate &&
    next.endDate === currentQuery.endDate &&
    next.responsavelId === currentQuery.responsavelId &&
    next.clientId === currentQuery.clientId
  )
}

// True quando as colunas visíveis da sprint ativa incluem alguma coluna de
// custom field (`cf:<uuid>`). Reusa o getter `visibleListColumns` (única
// fonte de verdade — já aplica race guard de sprint e ignora fields
// deletados/arquivados, AC-13/14), garantindo zero drift entre "o que a
// lista renderiza" e "quando pedimos valores ao backend". Pure function de
// `state` — testável sem store.
export function hasVisibleCustomFieldColumn(state, sprintId) {
  if (!state || !sprintId) return false
  if (state.activeSprintId !== sprintId) return false
  return visibleListColumns(state).some(function (col) {
    return col && typeof col.key === 'string' && col.key.indexOf('cf:') === 0
  })
}

// Monta o payload de `apiTarefas.getTasks` a partir do estado do Board.
// Data + boardFilters (responsavel/cliente/tags) são aplicados pelo backend;
// sempre encaminhados para que reloads pós-mutação preservem o filtro ativo.
// `includeCustomFields: true` é ADITIVO: só entra no payload quando as
// colunas visíveis da sprint ativa incluem uma coluna `cf:*` — sem isso a
// query é idêntica à atual (zero regressão).
// A flag de tarefa fechada (includeClosed, AC-9) também flui daqui para que o
// toggle "Mostrar fechadas" sobreviva a reloads.
export function buildTasksQueryFromState(state, sprintId) {
  const dateFilter = (state && state.dateFilter) || {}
  const boardFilters = (state && state.boardFilters) || {}
  const query = {
    sprintId: sprintId,
    startDate: dateFilter.startDate,
    endDate: dateFilter.endDate,
    responsavelIds: boardFilters.responsavelIds,
    clientIds: boardFilters.clientIds,
    tagIds: boardFilters.tagIds,
    includeClosed: state && state.includeClosed,
  }
  // `sprintId` ausente significa "recarrega a lista ATIVA" — e o que o modal de
  // detalhe despacha (`_reloadActiveTaskList` -> `loadTasks()` sem payload).
  // Sem este fallback, `hasVisibleCustomFieldColumn` cai no guard `!sprintId`,
  // a query sai sem a flag e o SET_TASKS seguinte substitui as tasks por
  // objetos sem `customFieldValues` — as celulas voltavam para '—' logo depois
  // de o usuario editar o valor no proprio modal. So a DECISAO da flag usa o
  // fallback: `query.sprintId` continua exatamente como veio, para nao mudar o
  // conjunto de tarefas buscado.
  const sprintIdForColumns = sprintId || (state && state.activeSprintId) || null
  if (hasVisibleCustomFieldColumn(state, sprintIdForColumns)) {
    query.includeCustomFields = true
  }
  return query
}

// Termo de busca já trimado. Termo só com espaços não conta como filtro ativo
// (AC-4.1) — é o mesmo guard de `_applySearchFilter` no getter.
function trimmedSearchQuery(state) {
  if (!state || typeof state.searchQuery !== 'string') return ''
  return state.searchQuery.trim()
}

// True quando QUALQUER uma das 7 fontes de filtro está ativa: os 3 board
// filters (responsável, cliente, tag), as 2 pontas da janela de datas, a busca
// e o filtro de status. COMPÕE `hasBoardFiltersActive` (que cobre só as 3
// primeiras) em vez de substituí-lo — o botão "Limpar filtros" continua com a
// mesma fonte de verdade.
// `includeClosed` NÃO entra: é escopo de view, não predicado `casa()`.
export function isAnyTaskFilterActive(state) {
  if (!state) return false
  if (hasBoardFiltersActive(state)) return true
  const dateFilter = state.dateFilter || {}
  if (dateFilter.startDate) return true
  if (dateFilter.endDate) return true
  if (trimmedSearchQuery(state)) return true
  return Array.isArray(state.statusFilter) && state.statusFilter.length > 0
}

// Serializa uma lista de ids em string estável — ordenada, para que reordenar
// o array (ex.: usuário desmarca e remarca um responsável) não conte como
// "mudou o conjunto de filtros".
function sortedIdsToSignature(values) {
  if (!Array.isArray(values) || values.length === 0) return ''
  return values.map(String).sort().join(SIGNATURE_VALUE_SEPARATOR)
}

// String estável do CONJUNTO de filtros ativos. Consumida como token de reset
// da expansão derivada (AC-6.4): pergunta nova ⇒ resposta aberta. Estável sob
// reordenação de array; muda quando o conjunto muda. Sem filtro ⇒ string
// vazia (nunca `undefined`).
export function taskFilterSignature(state) {
  if (!isAnyTaskFilterActive(state)) return ''
  const boardFilters = state.boardFilters || {}
  const dateFilter = state.dateFilter || {}
  return [
    'responsavel:' + sortedIdsToSignature(boardFilters.responsavelIds),
    'cliente:' + sortedIdsToSignature(boardFilters.clientIds),
    'tag:' + sortedIdsToSignature(boardFilters.tagIds),
    'data:' +
      (dateFilter.startDate || '') +
      SIGNATURE_RANGE_SEPARATOR +
      (dateFilter.endDate || ''),
    'busca:' + trimmedSearchQuery(state),
    'status:' + sortedIdsToSignature(state.statusFilter),
  ].join(SIGNATURE_FIELD_SEPARATOR)
}
